/**
 * Lyd-innstillinger med separate volum-nivåer for musikk, SFX og ambient.
 *
 * Bygd i runde 41 etter persona-test som etterspurte mer finkontroll på
 * lydbalansen. Master mute (`audioManager.toggleMute`) styrer fortsatt
 * alt; her er finregulering per kategori.
 *
 * Lagring: localStorage. Verdier i prosent (0–100) for å være enkle å
 * vise i UI; multipliseres med kategori-default ved anvendelse.
 *
 * Defaults speiler runde 38-balansen:
 * - musikk 25% (matcher VOLUM_STANDARD i audioManager)
 * - sfx 50%   (skala-multiplier på VOLUM_PER_NAVN i sfx.ts)
 * - ambient 30% (skala-multiplier på VOLUM_PER_NAVN i ambientAudio.ts)
 */

const STORAGE_KEY = 'forlagshuset:lyd-innstillinger';

const DEFAULT_MUSIKK = 25;
const DEFAULT_SFX = 50;
const DEFAULT_AMBIENT = 30;

export interface LydInnstillinger {
  musikkProsent: number;
  sfxProsent: number;
  ambientProsent: number;
}

type Lytter = (v: LydInnstillinger) => void;

function clamp(n: number): number {
  if (n < 0) return 0;
  if (n > 100) return 100;
  return Math.round(n);
}

function lesFraStorage(): LydInnstillinger {
  if (typeof localStorage === 'undefined') {
    return {
      musikkProsent: DEFAULT_MUSIKK,
      sfxProsent: DEFAULT_SFX,
      ambientProsent: DEFAULT_AMBIENT,
    };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        musikkProsent: DEFAULT_MUSIKK,
        sfxProsent: DEFAULT_SFX,
        ambientProsent: DEFAULT_AMBIENT,
      };
    }
    const data = JSON.parse(raw) as Partial<LydInnstillinger>;
    return {
      musikkProsent: clamp(data.musikkProsent ?? DEFAULT_MUSIKK),
      sfxProsent: clamp(data.sfxProsent ?? DEFAULT_SFX),
      ambientProsent: clamp(data.ambientProsent ?? DEFAULT_AMBIENT),
    };
  } catch {
    return {
      musikkProsent: DEFAULT_MUSIKK,
      sfxProsent: DEFAULT_SFX,
      ambientProsent: DEFAULT_AMBIENT,
    };
  }
}

function skrivTilStorage(v: LydInnstillinger): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
}

export class LydInnstillingerStore {
  private state: LydInnstillinger = lesFraStorage();
  private lyttere: Set<Lytter> = new Set();

  get snapshot(): LydInnstillinger {
    return { ...this.state };
  }

  /** Multipliser med base-volum for musikk (default 0.25). */
  get musikkMultiplikator(): number {
    return this.state.musikkProsent / 100;
  }

  /** Multipliser med base-volum for SFX (per-navn-defaults). */
  get sfxMultiplikator(): number {
    return this.state.sfxProsent / 100;
  }

  /** Multipliser med base-volum for ambient (per-loop-defaults). */
  get ambientMultiplikator(): number {
    return this.state.ambientProsent / 100;
  }

  settMusikk(prosent: number): void {
    this.state = { ...this.state, musikkProsent: clamp(prosent) };
    skrivTilStorage(this.state);
    this.varsel();
  }

  settSfx(prosent: number): void {
    this.state = { ...this.state, sfxProsent: clamp(prosent) };
    skrivTilStorage(this.state);
    this.varsel();
  }

  settAmbient(prosent: number): void {
    this.state = { ...this.state, ambientProsent: clamp(prosent) };
    skrivTilStorage(this.state);
    this.varsel();
  }

  /** Endre med +/- delta i prosent-poeng. Brukes av +/-knapper i UI. */
  endreMusikk(delta: number): void {
    this.settMusikk(this.state.musikkProsent + delta);
  }

  endreSfx(delta: number): void {
    this.settSfx(this.state.sfxProsent + delta);
  }

  endreAmbient(delta: number): void {
    this.settAmbient(this.state.ambientProsent + delta);
  }

  subscribe(lytter: Lytter): () => void {
    this.lyttere.add(lytter);
    return () => this.lyttere.delete(lytter);
  }

  private varsel(): void {
    for (const l of this.lyttere) {
      try {
        l(this.snapshot);
      } catch {
        // ignore
      }
    }
  }
}

export const lydInnstillinger = new LydInnstillingerStore();
